const DB_NAME = 'attendance_device';
const STORE_NAME = 'identity';
const KEY = 'device_token';
const COOKIE_KEY = 'attendance_device_id';
const COOKIE_EXPIRY_DAYS = 365;

// ── Cookie helpers ──────────────────────────────────────────────
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
}

function getCookie(name) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

// ── IndexedDB helpers ───────────────────────────────────────────
function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = () => reject(req.error);
  });
}

async function getFromDB(db) {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(KEY);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });
}

async function saveToDB(db, value) {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(value, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

// ── Fingerprint generator ───────────────────────────────────────
function generateFingerprint() {
  const raw = [
    navigator.userAgent,
    navigator.language,
    navigator.platform,
    window.screen.width,
    window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    navigator.deviceMemory || 0,
  ].join('|');

  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) - hash) + raw.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(16);
}

// ── Save to all storage locations ───────────────────────────────
function saveToAll(token) {
  try { localStorage.setItem('device_id', token); } catch(e) {}
  try { sessionStorage.setItem('device_id', token); } catch(e) {}
  try { setCookie(COOKIE_KEY, token, COOKIE_EXPIRY_DAYS); } catch(e) {}
}

// ── Get from any available storage ──────────────────────────────
function getFromAny() {
  return localStorage.getItem('device_id')
    || sessionStorage.getItem('device_id')
    || getCookie(COOKIE_KEY)
    || null;
}

// ── Main export ─────────────────────────────────────────────────
export async function getDeviceId() {
  try {
    const db = await openDB();
    let token = await getFromDB(db);

    if (!token) {
      // Try other storage locations before generating new
      token = getFromAny();
    }

    if (!token) {
      // Generate new fingerprint
      token = generateFingerprint();
    }

    // Save to all locations including IndexedDB
    await saveToDB(db, token);
    saveToAll(token);

    return token;
  } catch {
    // IndexedDB failed — fallback to other storage
    let token = getFromAny();
    if (!token) {
      token = generateFingerprint();
    }
    saveToAll(token);
    return token;
  }
}

export function getDeviceName() {
  const ua = navigator.userAgent;
  let os = 'Unknown OS';
  let browser = 'Unknown Browser';

  if (ua.includes('Windows NT 10')) os = 'Windows 10/11';
  else if (ua.includes('Windows NT 6.3')) os = 'Windows 8.1';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Linux')) os = 'Linux';

  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';

  return `${os} — ${browser}`;
}